import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '@/lib/cn';

/**
 * Button — la jerarquía de acciones del portal.
 *
 * Cuatro niveles, y el que los separa es el RELLENO, no el tono. Se decidió así
 * el 2026-09-03, al contar los botones del producto: había 141 naranjas frente
 * a 39 primarios, es decir, el acento era tres veces y media más frecuente que
 * el color principal — la definición exacta de un acento que ya no acentúa.
 *
 *   primary      navy relleno    la acción de la pantalla. Una por vista.
 *   secondary    borde navy      importante, pero no es el destino.
 *   ghost        sin caja        acciones menores y de fila.
 *   destructive  rojo relleno    lo irreversible. Doce en 510 era la
 *                                proporción correcta y no debería subir.
 *
 * El naranja NO está aquí a propósito: dentro de la aplicación no pinta
 * botones. Lo vigila tests/test_reparto_del_acento.py.
 */
const boton = cva(
  // Base. `select-none` porque un botón que se puede seleccionar como texto
  // deja rastro azul al hacer doble clic, y se nota en las tablas.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap select-none ' +
    'font-semibold rounded-sm border-2 border-transparent ' +
    'transition-[background-color,border-color,color,transform] duration-150 ' +
    'focus-visible:focus-anillo ' +
    // El desplazamiento al pulsar es la única animación del componente: confirma
    // que el dedo o el ratón llegaron. 1px, no más.
    'active:translate-y-px ' +
    'disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-on-brand hover:bg-brand-strong ' +
          'disabled:bg-line disabled:text-muted',
        secondary: 'border-brand-ink text-brand-ink bg-transparent hover:bg-brand hover:text-on-brand ' +
          'disabled:border-line disabled:text-muted disabled:hover:bg-transparent',
        ghost: 'text-ink-2 hover:bg-sunken hover:text-brand-ink ' +
          'disabled:text-muted disabled:hover:bg-transparent',
        destructive: 'bg-danger text-on-brand hover:brightness-90 ' +
          'disabled:bg-line disabled:text-muted',
      },
      size: {
        // En escritorio manda la densidad; en pantalla de dedo, los 44 px.
        //
        // Hasta el 2026-09-04 aquí ponía que esos 44 px «los pone el bloque
        // `pointer: coarse` del sistema», y era FALSO: ese bloque
        // (design-system.css) nombra `.btn`, `.icon-btn`, `.nav-item`… — clases
        // del vanilla que la capa React no lleva. Medido en Mi perfil con
        // puntero grueso: la cabecera del shell daba 44×44 y los botones de esta
        // pantalla se quedaban en 32 y 35. La regla se cumple ahora donde se
        // promete.
        sm: 'text-2 px-3 py-1.5 min-h-8 pointer-coarse:min-h-11',
        md: 'text-3 px-6 py-2.5 min-h-10 pointer-coarse:min-h-11',
      },
      block: { true: 'w-full', false: '' },
      iconOnly: { true: 'px-0 aspect-square', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false, iconOnly: false },
  },
);

/** Las mismas clases para un `<a>` que tiene que parecer un botón —«Empezar»,
 *  «Continuar»—. Es navegación, así que va en un enlace de verdad: se abre en
 *  otra pestaña y el Atrás del navegador vuelve. Añade `no-underline`, que la
 *  hoja no trae `preflight`. */
export const estiloDeBoton = boton;

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof boton> & {
    /** Deshabilita y muestra el indicador SIN cambiar el ancho del botón: si el
     *  botón se encoge al pulsarlo, lo que hay al lado da un salto. */
    loading?: boolean;
    /** Por qué está apagado. Se enseña al pasar por encima y viaja como nombre
     *  accesible. Un botón deshabilitado sin explicación es un callejón sin
     *  salida — y era el estado peor resuelto del sistema anterior, que además
     *  lo pintaba con `opacity:.55` y lo dejaba por debajo del contraste
     *  mínimo legible. */
    razonDeshabilitado?: string;
  };

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant, size, block, iconOnly, loading, razonDeshabilitado,
    disabled, children, ...props },
  ref,
) {
  const apagado = disabled || loading;
  return (
    <button
      ref={ref}
      disabled={apagado}
      // `title` y no solo `aria-label`: el motivo tiene que verse con el ratón,
      // no solo oírse con lector de pantalla.
      title={apagado ? razonDeshabilitado : props.title}
      aria-describedby={props['aria-describedby']}
      className={cn(boton({ variant, size, block, iconOnly }), className)}
      {...props}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {/* El texto se mantiene en su sitio mientras carga: solo se atenúa. Así
          el botón no cambia de tamaño y nada se recoloca a su alrededor. */}
      <span className={cn(loading && 'opacity-70')}>{children}</span>
      {loading && <span className="sr-only">Procesando…</span>}
    </button>
  );
});
